import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';

const BusinessLogin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    nit: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState('email');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/business-dashboard');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (loginMethod === 'email') {
      if (!formData?.email?.trim()) {
        newErrors.email = 'El email empresarial es requerido';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Ingrese un email válido';
      }
    } else {
      if (!formData?.nit?.trim()) {
        newErrors.nit = 'El NIT es requerido';
      } else if (!/^\d{9,10}$/?.test(formData?.nit?.replace(/\D/g, ''))) {
        newErrors.nit = 'El NIT debe tener 9-10 dígitos';
      }
    }

    if (!formData?.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // For NIT login, we would need to query the business_profiles table first
      // For now, use email login
      const email = loginMethod === 'email' ? formData?.email : `${formData?.nit}@bizznet.nit`;
      
      const { error } = await authService?.signIn(email, formData?.password);
      
      if (error) {
        throw error;
      }
      
      // Success - AuthContext will update user state, useEffect will handle navigation
      // DO NOT navigate manually here - causes race condition
    } catch (error) {
      setErrors({ 
        password: error?.message || 'Credenciales inválidas. Por favor verifica tu información.' 
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8 text-center">
          <Link 
            to="/login-selection" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-6"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Volver a selección</span>
          </Link>
          
          <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Icon name="Briefcase" size={40} color="#FFFFFF" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Panel Empresarial
          </h1>
          <p className="text-gray-600">
            Accede a tu cuenta empresarial de BIZZNET
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Login Method Selector */}
          <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setLoginMethod('email')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                loginMethod === 'email' ?'bg-white text-blue-600 shadow-sm' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              Email
            </button>
            <button
              type="button"
              onClick={() => setLoginMethod('nit')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                loginMethod === 'nit' ?'bg-white text-blue-600 shadow-sm' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              NIT
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email or NIT Input */}
            {loginMethod === 'email' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Empresarial
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="Mail" size={20} className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleChange}
                    placeholder="empresa@ejemplo.com"
                    className={`w-full pl-10 pr-4 py-3 border ${
                      errors?.email ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
                {errors?.email && (
                  <p className="mt-1 text-sm text-red-600">{errors?.email}</p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  NIT de la Empresa
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="Building" size={20} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="nit"
                    value={formData?.nit}
                    onChange={handleChange}
                    placeholder="900123456-7"
                    className={`w-full pl-10 pr-4 py-3 border ${
                      errors?.nit ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
                {errors?.nit && (
                  <p className="mt-1 text-sm text-red-600">{errors?.nit}</p>
                )}
              </div>
            )}

            {/* Password Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Lock" size={20} className="text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData?.password}
                  onChange={handleChange}
                  placeholder="Ingresa tu contraseña"
                  className={`w-full pl-10 pr-12 py-3 border ${
                    errors?.password ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <Icon 
                    name={showPassword ? 'EyeOff' : 'Eye'} 
                    size={20} 
                    className="text-gray-400 hover:text-gray-600"
                  />
                </button>
              </div>
              {errors?.password && (
                <p className="mt-1 text-sm text-red-600">{errors?.password}</p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData?.rememberMe}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">Mantener sesión activa</span>
              </label>
              
              <Link 
                to="/password-recovery-request" 
                state={{ accountType: 'business' }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Icon name="Loader2" size={20} className="animate-spin" />
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <span>Iniciar Sesión como Empresa</span>
                  <Icon name="ArrowRight" size={20} />
                </>
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              ¿No tienes una cuenta empresarial?{' '}
              <Link to="/business-registration" className="text-blue-600 hover:text-blue-700 font-medium">
                Regístrate aquí
              </Link>
            </p>
          </div>

          {/* Support Link */}
          <div className="mt-4 text-center">
            <Link 
              to="/support" 
              className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
            >
              <Icon name="HelpCircle" size={16} />
              <span>¿Necesitas ayuda? Contacta soporte</span>
            </Link>
          </div>

          {/* Enterprise Badge */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Icon name="Shield" size={16} className="text-blue-600" />
                <span>Seguridad empresarial</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Lock" size={16} className="text-blue-600" />
                <span>Datos protegidos</span>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Status Reminder */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-700">
                <strong>Recordatorio:</strong> Verifica que tu suscripción empresarial esté activa. 
                Las cuentas vencidas tienen acceso limitado.
              </p>
            </div>
          </div>
        </div>

        {/* Privacy Policy */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Al iniciar sesión, aceptas nuestros{' '}
            <Link to="/terms" className="text-blue-600 hover:underline">
              Términos y Condiciones
            </Link>
            {' '}y{' '}
            <Link to="/privacy" className="text-blue-600 hover:underline">
              Política de Privacidad
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BusinessLogin;